// src/utils/secureStorage.ts
// Secure wrapper for localStorage with basic encryption (AES)
// NOTE: For demo purposes only. In production, use a robust key management system and a vetted crypto library.

import * as CryptoJS from 'crypto-js';

const SECRET_KEY = '2a17fdb9e46e392b8e511c5ad9c2338a6b72d9c2e87b1d416d0c8c1fe29ab937';

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export type SecureStorage = {
	setItem: (key: string, value: any) => void;
	getItem: <T = any>(key: string) => T | null;
	removeItem: (key: string) => void;
	clear: () => void;
};

export const secureStorage: SecureStorage = {
	setItem: (key: string, value: any) => {
		if (!isBrowser) return;
		try {
			const stringValue =
				value === undefined ? '' : typeof value === 'string' ? value : JSON.stringify(value);
			const encrypted = CryptoJS.AES.encrypt(stringValue, SECRET_KEY).toString();
			localStorage.setItem(key, encrypted);
		} catch (e) {
			// fallback to plain storage if encryption fails
			try {
				localStorage.setItem(key, JSON.stringify(value));
			} catch {
				// swallow - localStorage might be unavailable
			}
		}
	},

	getItem: (key: string) => {
		if (!isBrowser) return null;
		try {
			const encrypted = localStorage.getItem(key);
			if (encrypted === null) return null;

			// Attempt AES decryption first
			try {
				const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
				const decrypted = bytes.toString(CryptoJS.enc.Utf8);
				if (decrypted === '') return '';
				try {
					return JSON.parse(decrypted) as any;
				} catch {
					return decrypted as any;
				}
			} catch (e) {
				// If decryption fails, try parsing the stored value as JSON
				try {
					return JSON.parse(encrypted) as any;
				} catch {
					return encrypted as any;
				}
			}
		} catch {
			return null;
		}
	},

	removeItem: (key: string) => {
		if (!isBrowser) return;
		try {
			localStorage.removeItem(key);
		} catch {
			// ignore
		}
	},

	clear: () => {
		if (!isBrowser) return;
		try {
			localStorage.clear();
		} catch {
			// ignore
		}
	},
};
